import type { ComputerMode } from "@rakazo/contracts";

export const TIER_IDS = ["free", "solo", "plus", "pro", "max"] as const;
export type TierId = (typeof TIER_IDS)[number];

/** Team bots share one computer, Private bots each have their own (`dedicated`). */
export type SlotKind = "team" | "private";
export type SlotUsage = Record<SlotKind, number>;

export const TIER_LIMITS: Readonly<Record<TierId, SlotUsage>> = {
  free: { team: 2, private: 0 },
  solo: { team: 3, private: 1 },
  plus: { team: 6, private: 3 },
  pro: { team: 15, private: 5 },
  max: { team: 30, private: 10 },
};

/** The first tier that may put Team and Private bots in one group. */
const FIRST_MIXED_GROUP_TIER: TierId = "plus";

const SLOT_KINDS: readonly SlotKind[] = ["team", "private"];

export function isTierId(value: unknown): value is TierId {
  return TIER_IDS.some((id) => id === value);
}

export function slotKind(mode: ComputerMode): SlotKind {
  return mode === "dedicated" ? "private" : "team";
}

export function slotUsage(modes: Iterable<ComputerMode>): SlotUsage {
  const usage: SlotUsage = { team: 0, private: 0 };
  for (const mode of modes) usage[slotKind(mode)] += 1;
  return usage;
}

/** The first slot kind (team before private) whose usage is above the tier's limit, or null. */
export function overLimitSlot(tier: TierId, usage: SlotUsage): SlotKind | null {
  const limits = TIER_LIMITS[tier];
  return SLOT_KINDS.find((kind) => usage[kind] > limits[kind]) ?? null;
}

export function mixedGroupsAllowed(tier: TierId): boolean {
  return TIER_IDS.indexOf(tier) >= TIER_IDS.indexOf(FIRST_MIXED_GROUP_TIER);
}

export function isMixedGroup(modes: readonly ComputerMode[]): boolean {
  return modes.includes("team") && modes.includes("dedicated");
}

export function groupCompositionAllowed(tier: TierId, modes: readonly ComputerMode[]): boolean {
  return !isMixedGroup(modes) || mixedGroupsAllowed(tier);
}

/** How many bots of each kind must be archived to fit the target tier. */
export function downgradeNeed(target: TierId, usage: SlotUsage): SlotUsage {
  const limits = TIER_LIMITS[target];
  return {
    team: Math.max(0, usage.team - limits.team),
    private: Math.max(0, usage.private - limits.private),
  };
}

/** What a person's plan allows and how much of it is taken, as the clients receive it. */
export type PlanSlots = {
  tier: TierId;
  limits: SlotUsage;
  usage: SlotUsage;
};

export function planSlots(tier: TierId, usage: SlotUsage): PlanSlots {
  return { tier, limits: { ...TIER_LIMITS[tier] }, usage: { ...usage } };
}

/**
 * Whether one more bot of this computer mode fits the plan. The server refuses when the live count
 * plus the new bot passes the limit, so `usage >= limit` is full, and a limit of 0 is not on the
 * plan at all.
 */
export type SlotAvailability = "open" | "none" | "full";

export function slotAvailability(
  plan: { limits: SlotUsage; usage: SlotUsage },
  mode: ComputerMode,
): SlotAvailability {
  const kind = slotKind(mode);
  if (plan.limits[kind] === 0) return "none";
  return plan.usage[kind] >= plan.limits[kind] ? "full" : "open";
}
